import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAnimes, deleteAnime, updateAnime } from '../redux/slices/animeSlice';
import ConfirmModal from './ConfirmModal';
import EditAnimeModal from './EditAnimeModal';

const AnimeTable = () => {
  const dispatch = useDispatch();
  const { items: animes, status, error } = useSelector((state) => state.animes);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingAnime, setEditingAnime] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  
  // Estado para el modal de confirmación
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [animeToDelete, setAnimeToDelete] = useState(null);
  
  // Estado para el modal de edición
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAnimes());
  }, [dispatch]);

  // Mostrar error si existe
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setShowError(true);
      // Ocultar el mensaje después de 5 segundos
      const timer = setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filtrar animes por término de búsqueda
  const filteredAnimes = animes && animes.length ? animes.filter(anime =>
    anime.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Calcular paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAnimes = filteredAnimes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAnimes.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleEdit = (anime) => {
    setEditingAnime(anime);
    setIsEditModalOpen(true);
  };

  const handleSaveAnime = async (data) => {
    try {
      await dispatch(updateAnime(data)).unwrap();
      setIsEditModalOpen(false);
      setEditingAnime(null);
      
      // Mostrar mensaje de éxito
      setErrorMessage('Anime actualizado exitosamente');
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage('');
      }, 3000);
      
      // Recargar la lista de animes
      dispatch(fetchAnimes());
    } catch (err) {
      console.error('Error al actualizar:', err);
      setErrorMessage('Error al actualizar el anime: ' + (err.message || ''));
      setShowError(true);
    }
  };
  
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingAnime(null);
  };

  const handleDelete = (anime) => {
    setAnimeToDelete(anime);
    setIsConfirmModalOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!animeToDelete) return;
    
    try {
      const resultAction = await dispatch(deleteAnime(animeToDelete.id));
      if (deleteAnime.rejected.match(resultAction)) {
        // La acción fue rechazada, mostrar mensaje de error
        setErrorMessage(resultAction.payload.message);
        setShowError(true);
        // Ocultar el mensaje después de 5 segundos
        setTimeout(() => {
          setShowError(false);
          setErrorMessage('');
        }, 5000);
      } else {
        // La acción fue exitosa, refrescar la lista
        dispatch(fetchAnimes());
      }
    } catch (err) {
      console.error('Error al eliminar:', err);
      setErrorMessage('Error al eliminar el anime');
      setShowError(true);
    } finally {
      // Cerrar el modal y limpiar el estado
      setIsConfirmModalOpen(false);
      setAnimeToDelete(null);
    }
  };
  
  const cancelDelete = () => {
    setIsConfirmModalOpen(false);
    setAnimeToDelete(null);
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`mx-1 px-3 py-1 rounded ${
            currentPage === i
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="space-y-4">
      {showError && (
        <div className="bg-red-600 text-white p-4 rounded-md mb-4 text-center font-bold">
          {errorMessage}
        </div>
      )}
      
      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="Confirmar eliminación"
        message={
          animeToDelete 
            ? `¿Estás seguro de que deseas eliminar el anime "${animeToDelete.name}"?\n\nRecuerda que si tiene temporadas asociadas, la eliminación no será posible.`
            : "¿Estás seguro de que deseas eliminar este anime?"
        }
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      
      {/* Modal de edición */}
      <EditAnimeModal 
        isOpen={isEditModalOpen}
        anime={editingAnime}
        onClose={closeEditModal}
        onSave={handleSaveAnime}
      />
      
      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar animes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-600 rounded-md px-4 py-2 w-64 bg-gray-800 text-gray-300"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-900 divide-y divide-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Título</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Género</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Año</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Temporadas</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {currentAnimes.length > 0 ? (
              currentAnimes.map(anime => (
                <tr key={anime.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{anime.id || 'ID no disponible'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-white font-medium">{anime.name || 'Nombre no disponible'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-4 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-800 text-blue-100">
                      {anime.genre || 'Género no disponible'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{anime.releaseYear || 'Año no disponible'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{anime.totalSeasons || 'Temporadas no disponibles'}</td>
                  <td className="px-6 py-4 whitespace-nowrap space-x-2">
                    <button
                      onClick={() => handleEdit(anime)}
                      className="text-blue-400 hover:text-blue-200"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(anime)}
                      className="text-red-400 hover:text-red-200"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-white">
                  {status === 'loading' ? 'Cargando...' : 'No hay animes disponibles'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <nav>
            <ul className="flex">
              {renderPagination()}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default AnimeTable;
